import { AbstractHistogram } from 'hdr-histogram-js'
import React from 'react'
import { connect } from 'react-redux'

import { Entity } from '../../app/entity'

import Chart from '../histogram.d3'

import BoxBody from '../../helper/boxBody'
import LargeNumber from '../../helper/large-number'
import Loading from '../../helper/loading'
import Milliseconds from '../../helper/milliseconds'
import { ApplicationState } from '../../redux/ApplicationState'
import { observeElement, unobserveElement } from '../../helper/resizeObserver'
import { HistogramView } from '../../domain/HistogramView'
import { HistogramRange, visitHdrHistogram } from '../../helper/visitHdrHistogram'

interface Props {
    loading: boolean
    histogram: AbstractHistogram | null
}

interface HistogramRangeOption {
    value: HistogramRange
    label: string
}

interface State {
    selectedRange: HistogramRangeOption['value']
}

const DEFAULT_HISTOGRAM_RANGE: HistogramRangeOption['value'] = '99'
const HISTOGRAM_RANGE_OPTIONS: HistogramRangeOption[] = [
    { value: 'max', label: 'all requests' },
    { value: '99.9', label: 'from 5% to 99.9%' },
    { value: '99', label: 'from 5% to 99%' },
    { value: '95', label: 'from 5% to 95%' },
]

export class HistogramComponent extends React.Component<Props, State> {
    public static displayName = 'Histogram'

    public chart
    public svg
    public resizeObserver

    public state: State = {
        selectedRange: DEFAULT_HISTOGRAM_RANGE,
    }

    public createChart() {
        this.chart = new Chart()

        const chartElement = this.getChartElement()
        if (!chartElement) {
            return
        }

        const parentNode = jQuery(chartElement).parent()
        this.chart.create(chartElement, parentNode.width(), 180, this.tickFormatterCallBack, 'Requests')
        this.updateChart()
    }

    public updateChart = () => {
        const histogramView = this.getHistogramView()

        if (!histogramView || !Array.isArray(histogramView.buckets)) {
            return
        }

        const buckets = histogramView.buckets
        if (buckets.length === 0) {
            return
        }

        const chartLowerBound = buckets[0].start
        const chartUpperBound = buckets[buckets.length - 1].end
        const markers: Array<{ start: number; end: number; type: string }> = []
        if (histogramView.markers) {
            const marker95 = histogramView.markers.find(marker => marker.name === '95%')?.value
            const marker99 = histogramView.markers.find(marker => marker.name === '99%')?.value
            const markerMax = histogramView.markers.find(marker => marker.name === 'Max')?.value
            const markerTimeExceeded = histogramView.markers.find(marker => marker.name === 'timeExceeded')?.value

            const lastBucket = buckets[buckets.length - 1]

            if (marker95 !== undefined && marker99 !== undefined && markerMax !== undefined) {
                markers.push({
                    start: chartLowerBound,
                    end: Math.min(marker95, chartUpperBound),
                    type: 'success',
                })

                if (marker95 < chartUpperBound && Math.max(marker95, chartLowerBound) !== Math.min(marker99, chartUpperBound)) {
                    markers.push({
                        start: Math.max(marker95, chartLowerBound),
                        end: Math.min(marker99, chartUpperBound),
                        type: 'warning',
                    })
                }
                if (marker99 < chartUpperBound && Math.max(marker99, chartLowerBound) !== Math.min(markerMax, chartUpperBound)) {
                    markers.push({
                        start: Math.max(marker99, chartLowerBound),
                        end: Math.min(markerMax, chartUpperBound),
                        type: 'danger',
                    })
                }
            }

            if (markerTimeExceeded !== undefined && chartUpperBound >= lastBucket.start) {
                markers.push({
                    start: lastBucket.start + 7,
                    end: lastBucket.end + 7,
                    type: 'timeExceeded',
                })
            }

            if (markers.length > 0) {
                // Make sure the last marker stretches until the end of the returned histogram range.
                markers[markers.length - 1] = {
                    ...markers[markers.length - 1],
                    end: Math.max(markers[markers.length - 1].end, chartUpperBound),
                }
            }
        }

        this.chart.update(
            [
                buckets.map(bucket => ({
                    x: new Date(bucket.start),
                    y: bucket.value,
                    end: new Date(bucket.end),
                })),
            ],
            markers,
            this.tickFormatterCallBack,
            this.tooltipMessageCallBack,
        )
    }

    public setResizeObserver = () => {
        const chartElement = this.getChartElement()
        if (chartElement) {
            const parentNode = jQuery(chartElement).parent()
            this.resizeObserver = observeElement(parentNode[0], this.rebuildChart)
        }
    }

    public rebuildChart = () => {
        this.destroyChart()
        this.createChart()
    }

    private destroyChart() {
        this.chart?.destroy() // remove svg-elements from dom
        this.chart = null // remove reference to the object
    }

    public componentDidMount() {
        if (!this.chart) {
            this.createChart()
        }
    }

    public componentDidUpdate(prevProps, prevState) {
        if (this.svg && !this.svg?.classList.contains('chart')) {
            this.createChart()
        }
        if (!this.chart) {
            return
        }

        if (!this.resizeObserver && this.chart) {
            this.setResizeObserver()
        }

        this.updateChart()
    }

    public componentWillUnmount() {
        this.chart?.destroy()
        const chartElement = this.getChartElement()
        if (chartElement) {
            const parentNode = jQuery(chartElement as string).parent()
            if (parentNode[0]) {
                unobserveElement(parentNode[0])
            }
        }
    }

    public getChartElement = () => {
        return this.svg
    }

    private getHeaderMarkerType = (markerName: string) => {
        if (markerName === '5%') {
            return 'success'
        }

        if (markerName === '95%') {
            return 'warning'
        }

        if (markerName === '99%' || markerName === '99.9%' || markerName === 'Max') {
            return 'danger'
        }

        if (markerName === 'timeExceeded') {
            return 'timeExceeded'
        }

        return 'success'
    }

    private getSelectedRangeOption = (): HistogramRangeOption => {
        return (
            HISTOGRAM_RANGE_OPTIONS.find(option => option.value === this.state.selectedRange) ||
            HISTOGRAM_RANGE_OPTIONS.find(option => option.value === DEFAULT_HISTOGRAM_RANGE) ||
            HISTOGRAM_RANGE_OPTIONS[0]
        )
    }

    private getHistogramView = (): HistogramView | null => {
        if (!this.props.histogram) {
            return null
        }

        return visitHdrHistogram(this.props.histogram, this.getSelectedRangeOption().value)
    }

    private handleRangeSelect = (rangeValue: HistogramRangeOption['value']) => {
        this.setState({
            selectedRange: rangeValue,
        })
    }

    public render() {
        if (!this.props.histogram) {
            return <Loading {...this.props} height={135} title="Response Time Distribution" />
        }

        const selectedRange = this.getSelectedRangeOption()
        const histogramView = this.getHistogramView()

        return (
            <div className="box box-default operation-histogram">
                <div className="box-header">
                    <nav className="navbar navbar-toolbar">
                        <div className="container-fluid">
                            <h3 className="box-title">
                                Response Time Distribution
                                <div className="dropdown">
                                    <span className="text-mute tw-font-size-14 tw-pl-16" data-toggle="dropdown" aria-expanded="false">
                                        <i className="fa fa-arrows-h text-muted" /> {selectedRange.label} <span className="caret" />
                                    </span>
                                    <ul className="dropdown-menu">
                                        {HISTOGRAM_RANGE_OPTIONS.map(option => (
                                            <li key={option.value} className={option.value === selectedRange.value ? 'active' : ''}>
                                                <a onClick={() => this.handleRangeSelect(option.value)}>{option.label}</a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </h3>

                            {histogramView?.markers ? (
                                <ul className="nav navbar-nav navbar-right navbar-numbers">
                                    {histogramView.markers.map(marker => {
                                        const markerType = this.getHeaderMarkerType(marker.name)

                                        return (
                                            <li key={marker.name} className={'histogram-marker histogram-marker-' + markerType}>
                                                <a>
                                                    <Milliseconds value={marker.value * 1000} />{' '}
                                                    <small>
                                                        <span className="histogram-marker-indicator" aria-hidden="true" />
                                                        {marker.name}
                                                    </small>
                                                </a>
                                            </li>
                                        )
                                    })}
                                    <li>
                                        <a>
                                            <LargeNumber value={histogramView.total} /> <small>Requests</small>
                                        </a>
                                    </li>
                                </ul>
                            ) : null}
                        </div>
                    </nav>
                </div>

                <BoxBody {...this.props}>
                    <div className="chart">
                        <svg ref={svg => (this.svg = svg)} />
                    </div>
                </BoxBody>
            </div>
        )
    }

    private tickFormatterCallBack = (d: Date) => {
        return d.getTime() + 'ms'
    }
    private tooltipMessageCallBack = (x: Date, end: Date) => {
        const tooltipTimeText =
            x.getTime() < 60000 ? this.tickFormatterCallBack(x) + ' - ' + this.tickFormatterCallBack(end) : 'Response Time'
        const timeExceededText = x.getTime() < 60000 ? '' : '60+ s'
        return [tooltipTimeText, timeExceededText]
    }
}

export default connect<Props, {}, {}>((globalState: ApplicationState, props) => {
    const histogram = globalState.operations.histogram[globalState.tidewaysContext.route.parameters.operation] || new Entity()

    if (!histogram.data?.report) {
        return {
            loading: true,
            histogram: null,
        }
    }

    return {
        loading: false,
        histogram: histogram.data.report as AbstractHistogram,
    }
})(HistogramComponent)
